import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaUserCircle, FaUserTie } from "react-icons/fa";
import {
  Bell,
  BellOff,
  BellRing,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Video,
  X,
} from "lucide-react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import api from "../../Services/api";
import { useOperador } from "../../Hooks/useOperador";
import "./styles.css";

const parseAgendaItems = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.agenda)) return data.agenda;
  if (Array.isArray(data?.tarefas)) return data.tarefas;
  return [];
};

const getAgendaId = (item) =>
  item?.IDAgendaTarefa || item?.IDAgenda || item?.id || item?.ID;

const normalizeValue = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

const isAcceptedTask = (item) =>
  normalizeValue(item?.tipo) === "tarefa" &&
  normalizeValue(item?.statusParticipanteOperador) === "aprovado";

const isDirectlyLinkedToOperator = (item, IDOperador) =>
  Number(item?.IDOperadorCriador) === Number(IDOperador) ||
  Number(item?.IDOperadorResponsavel) === Number(IDOperador) ||
  item?.vinculadoAoOperadorAtual === true ||
  ["aprovado", "pendente"].includes(normalizeValue(item?.statusParticipanteOperador));

const isGroupOnlyItem = (item, IDOperador) =>
  Boolean(item?.IDAgendaGrupo) && !isDirectlyLinkedToOperator(item, IDOperador);

const MenuSuperior = ({ userId, userName, cliente, removeMarginRight = false }) => {
  const { IDOperador, permissaoPrincipal } = useOperador();
  const navigate = useNavigate();
  const notificationCenterRef = useRef(null);
  const notificationStorageKey = `agendaNotificationsEnabled:${IDOperador || "anonymous"}`;
  const readNotificationsStorageKey = `agendaNotificationsRead:${IDOperador || "anonymous"}`;
  const [agendaItems, setAgendaItems] = useState([]);
  const [notificationCenterOpen, setNotificationCenterOpen] = useState(false);
  const [readNotificationKeys, setReadNotificationKeys] = useState(() => {
    try {
      return new Set(
        JSON.parse(
          window.localStorage.getItem(readNotificationsStorageKey) || "[]",
        ),
      );
    } catch {
      return new Set();
    }
  });
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    () =>
      typeof window !== "undefined" &&
      window.localStorage.getItem(notificationStorageKey) === "true",
  );
  const [notificationPermission, setNotificationPermission] = useState(
    () =>
      typeof window !== "undefined" && "Notification" in window
        ? window.Notification.permission
        : "unsupported",
  );

  const navbarStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "18px",
    width: "100%",
    minWidth: 0,
    minHeight: "76px",
    padding: "16px 24px",
    ...(removeMarginRight ? { marginRight: 0 } : null),
  };

  const textStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    minWidth: 0,
    lineHeight: 1.2,
  };

  const loadAgendaReminders = useCallback(async () => {
    if (!IDOperador) return;

    try {
      const isAdmin = Number(permissaoPrincipal) === 99;
      const response = await api.get(isAdmin ? "/agenda" : `/agenda/operador/${IDOperador}`, {
        params: { page: 1, limit: 9999, ...(isAdmin ? { visualizarTudoAgenda: 1 } : {}) },
      });
      setAgendaItems(parseAgendaItems(response));
    } catch (error) {
      console.error("Erro ao carregar lembretes da agenda.", error);
    }
  }, [IDOperador, permissaoPrincipal]);

  useEffect(() => {
    setNotificationsEnabled(
      window.localStorage.getItem(notificationStorageKey) === "true",
    );
    try {
      setReadNotificationKeys(
        new Set(
          JSON.parse(
            window.localStorage.getItem(readNotificationsStorageKey) || "[]",
          ),
        ),
      );
    } catch {
      setReadNotificationKeys(new Set());
    }
  }, [notificationStorageKey, readNotificationsStorageKey]);

  useEffect(() => {
    loadAgendaReminders();
    const intervalId = window.setInterval(loadAgendaReminders, 60000);

    const refreshWhenVisible = () => {
      if (document.visibilityState === "visible") loadAgendaReminders();
    };
    document.addEventListener("visibilitychange", refreshWhenVisible);
    window.addEventListener("agenda:notifications-refresh", loadAgendaReminders);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
      window.removeEventListener("agenda:notifications-refresh", loadAgendaReminders);
    };
  }, [loadAgendaReminders]);

  const activeAgendaItems = useMemo(() => {
    const now = Date.now();

    return agendaItems.filter((item) => {
      const start = new Date(item.dataInicio).getTime();
      const status = normalizeValue(item.statusKanban);
      const participantStatus = normalizeValue(item.statusParticipanteOperador);
      const isOwner =
        Number(item.IDOperadorCriador) === Number(IDOperador) ||
        Number(item.IDOperadorResponsavel) === Number(IDOperador);
      const canReceiveReminder =
        isOwner || !participantStatus || participantStatus === "aprovado";

      return (
        Number.isFinite(start) &&
        start > now - 120000 &&
        !["concluido", "cancelado"].includes(status) &&
        canReceiveReminder
      );
    });
  }, [agendaItems, IDOperador]);

  const getNotificationKey = useCallback(
    (item) => `${item.notificationKind || "reminder"}:${getAgendaId(item)}:${new Date(item.dataInicio).toISOString()}`,
    [],
  );

  const notificationCenterItems = useMemo(() => {
    const now = Date.now();
    const nextWeek = now + 7 * 24 * 60 * 60 * 1000;

    const upcomingReminders = activeAgendaItems
      .filter((item) => {
        const start = new Date(item.dataInicio).getTime();
        return start >= now && start <= nextWeek;
      })
      .sort(
        (a, b) =>
          new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime(),
      )
      .map((item) => ({ ...item, notificationKind: "reminder" }));

    const acceptedTasks = agendaItems
      .filter((item) => {
        const start = new Date(item.dataInicio).getTime();
        const status = normalizeValue(item.statusKanban);
        return (
          isAcceptedTask(item) &&
          Number.isFinite(start) &&
          !["concluido", "cancelado"].includes(status)
        );
      })
      .sort(
        (a, b) =>
          new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime(),
      )
      .map((item) => ({ ...item, notificationKind: "accepted-task" }));

    const adminCreatedItems = agendaItems
      .filter(
        (item) =>
          Number(permissaoPrincipal) === 99 &&
          Number(item.notificarAdmins || 0) === 1 &&
          !["concluido", "cancelado"].includes(normalizeValue(item.statusKanban)),
      )
      .map((item) => ({ ...item, notificationKind: "admin-created" }));

    const notificationsByKey = new Map();
    [...adminCreatedItems, ...acceptedTasks, ...upcomingReminders].forEach((item) => {
      notificationsByKey.set(getAgendaId(item), item);
    });

    return Array.from(notificationsByKey.values())
      .sort(
        (a, b) =>
          new Date(b.dataInicio).getTime() - new Date(a.dataInicio).getTime(),
      )
      .slice(0, 6);
  }, [activeAgendaItems, agendaItems, permissaoPrincipal]);

  const unreadCount = useMemo(
    () =>
      notificationCenterItems.filter(
        (item) => !readNotificationKeys.has(getNotificationKey(item)),
      ).length,
    [getNotificationKey, notificationCenterItems, readNotificationKeys],
  );

  useEffect(() => {
    if (!notificationCenterOpen) return;

    const closeOnOutsideClick = (event) => {
      if (!notificationCenterRef.current?.contains(event.target)) {
        setNotificationCenterOpen(false);
      }
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setNotificationCenterOpen(false);
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [notificationCenterOpen]);

  useEffect(() => {
    if (!notificationsEnabled) return;

    const now = Date.now();
    activeAgendaItems.forEach((item) => {
      const start = new Date(item.dataInicio);
      const minutesUntil = (start.getTime() - now) / 60000;
      const isMeeting = normalizeValue(item.tipo) === "reuniao";
      const reminderMinutes = isMeeting ? 15 : 30;

      if (minutesUntil > reminderMinutes || minutesUntil < -2) return;

      const reminderKey = `agendaReminder:${IDOperador}:${getAgendaId(item)}:${start.toISOString()}:${reminderMinutes}`;
      if (window.localStorage.getItem(reminderKey)) return;
      window.localStorage.setItem(reminderKey, new Date().toISOString());

      const timeLabel = start.toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      });
      const title = isMeeting ? "Reuniao em breve" : "Compromisso em breve";
      const body = `${item.titulo} - ${timeLabel}`;

      if (
        notificationPermission === "granted" &&
        "Notification" in window
      ) {
        const notification = new window.Notification(title, {
          body,
          icon: "/logo192.png",
          tag: reminderKey,
        });
        notification.onclick = () => {
          try {
            const readKeys = new Set(
              JSON.parse(
                window.localStorage.getItem(readNotificationsStorageKey) ||
                  "[]",
              ),
            );
            readKeys.add(
              `${getAgendaId(item)}:${new Date(item.dataInicio).toISOString()}`,
            );
            window.localStorage.setItem(
              readNotificationsStorageKey,
              JSON.stringify(Array.from(readKeys)),
            );
          } catch (error) {
            console.error("Erro ao marcar notificacao como lida.", error);
          }
          window.focus();
          window.location.assign(`/Agenda?item=${getAgendaId(item)}`);
          notification.close();
        };
        return;
      }

      Swal.fire({
        toast: true,
        icon: isMeeting ? "info" : "warning",
        title,
        text: body,
        position: "top-end",
        showConfirmButton: false,
        timer: 8000,
        timerProgressBar: true,
      });
    });
  }, [
    activeAgendaItems,
    IDOperador,
    notificationPermission,
    notificationsEnabled,
    readNotificationsStorageKey,
  ]);

  const toggleNotifications = async () => {
    if (!("Notification" in window)) {
      setNotificationsEnabled((current) => {
        const enabled = !current;
        window.localStorage.setItem(notificationStorageKey, String(enabled));
        return enabled;
      });
      Swal.fire(
        "Lembretes do sistema",
        "Este navegador nao oferece notificacoes nativas. Os avisos serao exibidos dentro do PentaGov.",
        "info",
      );
      return;
    }

    let permission = window.Notification.permission;
    if (permission === "default") {
      permission = await window.Notification.requestPermission();
      setNotificationPermission(permission);
    }

    if (permission === "denied") {
      Swal.fire(
        "Notificacoes bloqueadas",
        "Permita notificacoes para o PentaGov nas configuracoes do navegador.",
        "warning",
      );
      return;
    }

    const enabled = !notificationsEnabled;
    setNotificationsEnabled(enabled);
    window.localStorage.setItem(notificationStorageKey, String(enabled));
    Swal.fire({
      toast: true,
      icon: enabled ? "success" : "info",
      title: enabled ? "Lembretes ativados" : "Lembretes pausados",
      position: "top-end",
      showConfirmButton: false,
      timer: 2500,
    });
  };

  const formatNotificationDate = (value) => {
    const date = new Date(value);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const sameDay = (first, second) =>
      first.getFullYear() === second.getFullYear() &&
      first.getMonth() === second.getMonth() &&
      first.getDate() === second.getDate();
    const dayLabel = sameDay(date, today)
      ? "Hoje"
      : sameDay(date, tomorrow)
        ? "Amanha"
        : date.toLocaleDateString("pt-BR", {
            day: "2-digit",
            month: "short",
          });
    const timeLabel = date.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });

    return `${dayLabel}, ${timeLabel}`;
  };

  const openAgenda = () => {
    setNotificationCenterOpen(false);
    navigate("/Agenda");
  };

  const markNotificationsAsRead = useCallback(
    (itemsToRead) => {
      setReadNotificationKeys((current) => {
        const next = new Set(current);
        itemsToRead.forEach((item) => next.add(getNotificationKey(item)));
        window.localStorage.setItem(
          readNotificationsStorageKey,
          JSON.stringify(Array.from(next)),
        );
        return next;
      });
    },
    [getNotificationKey, readNotificationsStorageKey],
  );

  const toggleNotificationCenter = () => {
    setNotificationCenterOpen((current) => {
      const opening = !current;
      if (opening) markNotificationsAsRead(notificationCenterItems);
      return opening;
    });
  };

  const openAgendaItem = (item) => {
    markNotificationsAsRead([item]);
    setNotificationCenterOpen(false);
    navigate(`/Agenda?item=${getAgendaId(item)}`);
  };

  return (
    <div className="menu-superior" style={navbarStyle}>
      <div className="menu-superior__group">
        <FaUserCircle className="menu-superior__icon" />
        <div className="menu-superior__text" style={textStyle}>
          <span className="menu-superior__label">Operador</span>
          <span className="menu-superior__value">{userId} - {userName}</span>
        </div>
      </div>
      <div className="menu-superior__actions">
        {cliente && (
          <div className="menu-superior__group menu-superior__group--cliente">
            <FaUserTie className="menu-superior__icon" />
            <div className="menu-superior__text" style={textStyle}>
              <span className="menu-superior__label">Contexto ativo</span>
              <span className="menu-superior__value">{cliente}</span>
            </div>
          </div>
        )}
        <div className="menu-superior__notification-wrap" ref={notificationCenterRef}>
          <button
            type="button"
            className={`menu-superior__notification ${notificationCenterOpen ? "is-open" : ""}`}
            onClick={toggleNotificationCenter}
            aria-label="Abrir central de notificacoes"
            aria-expanded={notificationCenterOpen}
            title="Central de notificacoes"
          >
            {notificationPermission === "denied" ? (
              <BellOff size={20} />
            ) : notificationsEnabled ? (
              <BellRing size={20} />
            ) : (
              <Bell size={20} />
            )}
            {unreadCount > 0 && (
              <span aria-label={`${unreadCount} notificacoes nao lidas`}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          {notificationCenterOpen && (
            <section className="menu-superior__notification-center" aria-label="Central de notificacoes">
              <header>
                <div>
                  <span>Notificacoes</span>
                  <strong>Proximos compromissos</strong>
                </div>
                <button
                  type="button"
                  onClick={() => setNotificationCenterOpen(false)}
                  aria-label="Fechar central de notificacoes"
                  title="Fechar"
                >
                  <X size={18} />
                </button>
              </header>

              <div className="menu-superior__notification-settings">
                <div>
                  {notificationsEnabled ? <BellRing size={17} /> : <BellOff size={17} />}
                  <span>
                    <strong>Lembretes</strong>
                    <small>{notificationsEnabled ? "Ativados" : "Pausados"}</small>
                  </span>
                </div>
                <div>
                  <button
                    type="button"
                    className={`notification-toggle ${notificationsEnabled ? "is-enabled" : ""}`}
                    onClick={toggleNotifications}
                    aria-label={
                      notificationsEnabled
                        ? "Pausar lembretes da agenda"
                        : "Ativar lembretes da agenda"
                    }
                  >
                    {notificationsEnabled ? (
                      <BellOff size={15} />
                    ) : (
                      <BellRing size={15} />
                    )}
                    <span>{notificationsEnabled ? "Pausar" : "Ativar"}</span>
                  </button>
                </div>
              </div>

              <div className="menu-superior__notification-list">
                {notificationCenterItems.map((item) => {
                  const isMeeting = normalizeValue(item.tipo) === "reuniao";
                  const acceptedTask = item.notificationKind === "accepted-task";
                  const adminCreated = item.notificationKind === "admin-created";
                  const groupOnly = isGroupOnlyItem(item, IDOperador);
                  const linkedTask = !adminCreated && !acceptedTask && !groupOnly && normalizeValue(item.tipo) === "tarefa";
                  return (
                    <button type="button" key={`${item.notificationKind}:${getAgendaId(item)}`} className={linkedTask ? "is-linked-task" : ""} onClick={() => openAgendaItem(item)}>
                      <span className={linkedTask ? "is-linked-task" : isMeeting ? "is-meeting" : acceptedTask ? "is-accepted" : ""}>
                        {acceptedTask || adminCreated ? <CheckCircle2 size={16} /> : isMeeting ? <Video size={16} /> : <CalendarClock size={16} />}
                      </span>
                      <div>
                        <strong>{item.titulo}</strong>
                        <small>
                          {adminCreated
                            ? `Nova demanda de operador - ${formatNotificationDate(item.dataInicio)}`
                            : acceptedTask
                            ? `Tarefa aceita - ${formatNotificationDate(item.dataInicio)}`
                            : groupOnly
                            ? `Grupo - ${formatNotificationDate(item.dataInicio)}`
                            : linkedTask
                            ? `Tarefa vinculada - ${formatNotificationDate(item.dataInicio)}`
                            : formatNotificationDate(item.dataInicio)}
                        </small>
                      </div>
                      <ChevronRight size={16} />
                    </button>
                  );
                })}
                {notificationCenterItems.length === 0 && (
                  <div className="menu-superior__notification-empty">
                    <CalendarClock size={22} />
                    <span>Nenhum compromisso nos proximos 7 dias.</span>
                  </div>
                )}
              </div>

              <footer>
                <button type="button" onClick={openAgenda}>
                  Abrir Agenda <ChevronRight size={16} />
                </button>
              </footer>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default MenuSuperior;
